import { useState } from "react";
import type { ReactNode } from "react";
import { Link, useNavigate } from "react-router";
import { generatePassword } from "@passwordflow/crypto";
import { ArrowRight, Copy, RefreshCw, Wand2 } from "lucide-react";
import { useSecretCopy } from "../../app/clipboard.js";
import { paths } from "../../app/paths.js";
import { PasswordGlyphs, StrengthMeter } from "../../ui/forms.js";
import { Button, Card, CardHeader, IconButton } from "../../ui/primitives.js";

const MIN = 12;
const MAX = 48;

function fresh(length: number): string {
  return generatePassword({ length, sets: ["lower", "upper", "digits", "symbols"] });
}

/** Compact generator: roll, copy, or hand the result to a new entry without leaving the dashboard. */
export function QuickGenerator(): ReactNode {
  const navigate = useNavigate();
  const copy = useSecretCopy();
  const [length, setLength] = useState(24);
  const [password, setPassword] = useState((): string => fresh(24));

  return (
    <Card className="flex h-full flex-col">
      <CardHeader
        title="Schnell-Generator"
        description="Alle Zeichensätze, kryptografisch zufällig."
        icon={Wand2}
        action={
          <Link to={paths.generator} className="text-sm text-mist transition-colors hover:text-flow">
            Optionen
          </Link>
        }
      />
      <div className="flex flex-1 flex-col gap-4 p-5">
        <div className="flex items-start gap-2 rounded-lg border border-line-strong bg-ink px-3.5 py-3">
          <p className="min-w-0 flex-1 text-[0.95rem]" aria-live="polite">
            <PasswordGlyphs value={password} />
          </p>
          <IconButton label="Neu würfeln" icon={RefreshCw} size="sm" onClick={(): void => setPassword(fresh(length))} />
        </div>
        <StrengthMeter password={password} />
        <div>
          <div className="flex items-baseline justify-between text-[0.8125rem]">
            <label htmlFor="quick-gen-length" className="text-mist">
              Länge
            </label>
            <span className="font-mono font-semibold tabular-nums text-flow">{length}</span>
          </div>
          <input
            id="quick-gen-length"
            type="range"
            min={MIN}
            max={MAX}
            value={length}
            onChange={(event): void => {
              const next = Number(event.target.value);
              setLength(next);
              setPassword(fresh(next));
            }}
            className="mt-2 w-full accent-[#62F0D8]"
          />
        </div>
        <div className="mt-auto flex flex-wrap gap-2">
          <Button icon={Copy} onClick={(): void => void copy(password, "Passwort kopiert")}>
            Kopieren
          </Button>
          <Button variant="ghost" onClick={(): void => void navigate(paths.vaultNew, { state: { password } })}>
            Als Eintrag
            <ArrowRight size={15} aria-hidden="true" />
          </Button>
        </div>
      </div>
    </Card>
  );
}
